import { useState, useEffect } from "react";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ClipboardList, FileText, FolderOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminTemplates = () => {
  const { t } = useLanguage();
  
  const FIELD_TYPES = [
    { value: "text", label: t("short_text") },
    { value: "textarea", label: t("long_text") },
    { value: "select", label: t("selection") },
    { value: "checkbox", label: t("checkbox") },
  ];
  
  // Categories
  const [anamnesisCategories, setAnamnesisCategories] = useState([]);
  const [consentCategories, setConsentCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryType, setCategoryType] = useState("anamnesis");
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", order: 0 });
  const [deleteCategory, setDeleteCategory] = useState(null);
  
  // Anamnesis
  const [anamnesisTemplates, setAnamnesisTemplates] = useState([]);
  const [anamnesisDialogOpen, setAnamnesisDialogOpen] = useState(false);
  const [editingAnamnesis, setEditingAnamnesis] = useState(null);
  const [anamnesisForm, setAnamnesisForm] = useState({
    name: "",
    description: "",
    category_id: "",
    fields: [],
  });
  
  // Consent
  const [consentTemplates, setConsentTemplates] = useState([]);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [editingConsent, setEditingConsent] = useState(null);
  const [consentForm, setConsentForm] = useState({
    name: "",
    content: "",
    category_id: "",
  });
  
  const [deleteItem, setDeleteItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [aRes, cRes, aCatRes, cCatRes] = await Promise.all([
        axios.get(`${API}/admin/anamnesis-templates`),
        axios.get(`${API}/admin/consent-templates`),
        axios.get(`${API}/admin/template-categories?type=anamnesis`),
        axios.get(`${API}/admin/template-categories?type=consent`),
      ]);
      setAnamnesisTemplates(aRes.data);
      setConsentTemplates(cRes.data);
      setAnamnesisCategories(aCatRes.data);
      setConsentCategories(cCatRes.data);
    } catch (error) {
      toast.error("Erro ao carregar templates");
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const openCategoryDialog = (type, category = null) => {
    setCategoryType(type);
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || "",
        order: category.order || 0
      });
    } else {
      setEditingCategory(null);
      const currentCategories = type === "anamnesis" ? anamnesisCategories : consentCategories;
      setCategoryForm({ name: "", description: "", order: currentCategories.length });
    }
    setCategoryDialogOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Nome da categoria é obrigatório");
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/template-categories/${editingCategory.id}`, {
          ...categoryForm,
          type: categoryType
        });
        toast.success("Categoria atualizada!");
      } else {
        await axios.post(`${API}/admin/template-categories`, {
          ...categoryForm,
          type: categoryType
        });
        toast.success("Categoria criada!");
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar categoria");
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;
    try {
      await axios.delete(`${API}/admin/template-categories/${deleteCategory.id}`);
      toast.success("Categoria excluída!");
      setDeleteCategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir categoria");
    }
  };

  // Anamnesis handlers
  const openAnamnesisDialog = (template = null) => {
    if (template) {
      setEditingAnamnesis(template);
      setAnamnesisForm({
        name: template.name,
        description: template.description || "",
        category_id: template.category_id || "",
        fields: template.fields,
      });
    } else {
      setEditingAnamnesis(null);
      setAnamnesisForm({
        name: "",
        description: "",
        category_id: "",
        fields: [],
      });
    }
    setAnamnesisDialogOpen(true);
  };

  const addField = () => {
    setAnamnesisForm({
      ...anamnesisForm,
      fields: [
        ...anamnesisForm.fields,
        {
          id: `field-${Date.now()}`,
          label: "",
          field_type: "text",
          options: [],
          required: false,
        },
      ],
    });
  };

  const updateField = (index, field, value) => {
    const updated = [...anamnesisForm.fields];
    updated[index] = { ...updated[index], [field]: value };
    setAnamnesisForm({ ...anamnesisForm, fields: updated });
  };

  const removeField = (index) => {
    setAnamnesisForm({
      ...anamnesisForm,
      fields: anamnesisForm.fields.filter((_, i) => i !== index),
    });
  };

  const saveAnamnesis = async () => {
    if (!anamnesisForm.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    
    try {
      const formData = {
        ...anamnesisForm,
        category_id: anamnesisForm.category_id || null
      };
      if (editingAnamnesis) {
        await axios.put(`${API}/admin/anamnesis-templates/${editingAnamnesis.id}`, formData);
        toast.success("Template atualizado!");
      } else {
        await axios.post(`${API}/admin/anamnesis-templates`, formData);
        toast.success("Template criado!");
      }
      setAnamnesisDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar template");
    }
  };

  // Consent handlers
  const openConsentDialog = (template = null) => {
    if (template) {
      setEditingConsent(template);
      setConsentForm({
        name: template.name,
        content: template.content,
        category_id: template.category_id || "",
      });
    } else {
      setEditingConsent(null);
      setConsentForm({
        name: "",
        content: "",
        category_id: "",
      });
    }
    setConsentDialogOpen(true);
  };

  const saveConsent = async () => {
    if (!consentForm.name.trim() || !consentForm.content.trim()) {
      toast.error("Nome e conteúdo são obrigatórios");
      return;
    }
    
    try {
      const formData = {
        ...consentForm,
        category_id: consentForm.category_id || null
      };
      if (editingConsent) {
        await axios.put(`${API}/admin/consent-templates/${editingConsent.id}`, formData);
        toast.success("Termo atualizado!");
      } else {
        await axios.post(`${API}/admin/consent-templates`, formData);
        toast.success("Termo criado!");
      }
      setConsentDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar termo");
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteItem) return;
    
    try {
      if (deleteItem.type === "anamnesis") {
        await axios.delete(`${API}/admin/anamnesis-templates/${deleteItem.id}`);
      } else {
        await axios.delete(`${API}/admin/consent-templates/${deleteItem.id}`);
      }
      toast.success("Template excluído!");
      setDeleteItem(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir template");
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="admin-templates-page">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("administration")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl">{t("templates")}</h1>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="anamnesis" className="w-full">
          <TabsList className="w-full grid grid-cols-3 mb-6">
            <TabsTrigger value="anamnesis" className="font-mono uppercase text-xs tracking-widest" data-testid="anamnesis-tab">
              {t("anamnesis_records")}
            </TabsTrigger>
            <TabsTrigger value="consent" className="font-mono uppercase text-xs tracking-widest" data-testid="consent-tab">
              {t("consent_terms")}
            </TabsTrigger>
            <TabsTrigger value="categories" className="font-mono uppercase text-xs tracking-widest" data-testid="categories-tab">
              {t("categories")}
            </TabsTrigger>
          </TabsList>

          {/* Anamnesis Tab */}
          <TabsContent value="anamnesis">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl">{t("anamnesis_models")}</h2>
              <Dialog open={anamnesisDialogOpen} onOpenChange={setAnamnesisDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="btn-primary" 
                    onClick={() => openAnamnesisDialog()}
                    data-testid="new-anamnesis-template"
                  >
                    <Plus size={16} className="mr-2" />
                    {t("new_model")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">
                      {editingAnamnesis ? t("edit_model") : t("new_anamnesis_model")}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div>
                      <Label className="label-mono">{t("model_name")}</Label>
                      <Input
                        value={anamnesisForm.name}
                        onChange={(e) => setAnamnesisForm({ ...anamnesisForm, name: e.target.value })}
                        className="input-paper"
                        placeholder={t("model_name_placeholder")}
                        data-testid="anamnesis-name-input"
                      />
                    </div>

                    <div>
                      <Label className="label-mono">{t("description")}</Label>
                      <Input
                        value={anamnesisForm.description}
                        onChange={(e) => setAnamnesisForm({ ...anamnesisForm, description: e.target.value })}
                        className="input-paper"
                        placeholder={t("description_placeholder")}
                      />
                    </div>

                    <div>
                      <Label className="label-mono">{t("select_template_category")}</Label>
                      <Select
                        value={anamnesisForm.category_id || "none"}
                        onValueChange={(value) => setAnamnesisForm({ ...anamnesisForm, category_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("uncategorized")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("uncategorized")}</SelectItem>
                          {anamnesisCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <Label className="label-mono">{t("fields")}</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addField}>
                          <Plus size={14} className="mr-1" />
                          {t("add")}
                        </Button>
                      </div>

                      <div className="space-y-4">
                        {anamnesisForm.fields.map((field, index) => (
                          <div key={field.id} className="border p-4 space-y-3">
                            <div className="flex items-start justify-between">
                              <span className="font-mono text-xs text-muted-foreground">
                                {t("field")} {index + 1}
                              </span>
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeField(index)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>

                            <Input
                              value={field.label}
                              onChange={(e) => updateField(index, "label", e.target.value)}
                              placeholder={t("field_name")}
                            />

                            <Select
                              value={field.field_type}
                              onValueChange={(value) => updateField(index, "field_type", value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FIELD_TYPES.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {(field.field_type === "select" || field.field_type === "checkbox") && (
                              <div>
                                <Label className="text-xs text-muted-foreground">
                                  {t("options_comma")}
                                </Label>
                                <Input
                                  value={field.options?.join(", ") || ""}
                                  onChange={(e) => updateField(
                                    index, 
                                    "options", 
                                    e.target.value.split(",").map(o => o.trim()).filter(Boolean)
                                  )}
                                  placeholder={t("options_placeholder")}
                                />
                              </div>
                            )}

                            {/* Required checkbox */}
                            <div className="flex items-center gap-2 pt-2 border-t">
                              <input
                                type="checkbox"
                                id={`required-${field.id}`}
                                checked={field.required || false}
                                onChange={(e) => updateField(index, "required", e.target.checked)}
                                className="w-4 h-4 accent-primary"
                              />
                              <Label htmlFor={`required-${field.id}`} className="text-sm cursor-pointer">
                                Campo obrigatório
                              </Label>
                            </div>
                          </div>
                        ))}

                        {anamnesisForm.fields.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-8">
                            {t("no_fields")}
                          </p>
                        )}
                      </div>
                    </div>

                    <Button 
                      onClick={saveAnamnesis} 
                      className="btn-primary w-full"
                      data-testid="save-anamnesis-template"
                    >
                      {editingAnamnesis ? t("save_changes") : t("create_model")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 loading-skeleton" />
                ))}
              </div>
            ) : anamnesisTemplates.length > 0 ? (
              <div className="space-y-4">
                {anamnesisTemplates.map((template) => (
                  <div 
                    key={template.id} 
                    className="bento-item flex items-center justify-between"
                    data-testid={`anamnesis-template-${template.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <ClipboardList size={20} strokeWidth={1} />
                      <div>
                        <h4 className="font-serif text-lg">{template.name}</h4>
                        <p className="text-xs font-mono text-muted-foreground">
                          {template.category_name && <span className="text-primary">{template.category_name} • </span>}
                          {template.fields?.length || 0} {t("fields")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAnamnesisDialog(template)}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteItem({ ...template, type: "anamnesis" })}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state border py-12">
                <ClipboardList className="empty-state-icon" />
                <p className="empty-state-title">{t("no_model_created")}</p>
              </div>
            )}
          </TabsContent>

          {/* Consent Tab */}
          <TabsContent value="consent">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl">{t("consent_terms_admin")}</h2>
              <Dialog open={consentDialogOpen} onOpenChange={setConsentDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="btn-primary" 
                    onClick={() => openConsentDialog()}
                    data-testid="new-consent-template"
                  >
                    <Plus size={16} className="mr-2" />
                    {t("new_term")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">
                      {editingConsent ? t("edit_term") : t("new_consent_term")}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div>
                      <Label className="label-mono">{t("model_name")}</Label>
                      <Input
                        value={consentForm.name}
                        onChange={(e) => setConsentForm({ ...consentForm, name: e.target.value })}
                        className="input-paper"
                        placeholder={t("model_name_placeholder")}
                        data-testid="consent-name-input"
                      />
                    </div>

                    <div>
                      <Label className="label-mono">{t("select_template_category")}</Label>
                      <Select
                        value={consentForm.category_id || "none"}
                        onValueChange={(value) => setConsentForm({ ...consentForm, category_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("uncategorized")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("uncategorized")}</SelectItem>
                          {consentCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="label-mono">{t("term_content")}</Label>
                      <Textarea
                        value={consentForm.content}
                        onChange={(e) => setConsentForm({ ...consentForm, content: e.target.value })}
                        placeholder={t("term_content_placeholder")}
                        rows={12}
                        data-testid="consent-content-input"
                      />
                    </div>

                    <Button 
                      onClick={saveConsent} 
                      className="btn-primary w-full"
                      data-testid="save-consent-template"
                    >
                      {editingConsent ? t("save_changes") : t("create_term")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 loading-skeleton" />
                ))}
              </div>
            ) : consentTemplates.length > 0 ? (
              <div className="space-y-4">
                {consentTemplates.map((template) => (
                  <div 
                    key={template.id} 
                    className="bento-item flex items-center justify-between"
                    data-testid={`consent-template-${template.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <FileText size={20} strokeWidth={1} />
                      <div>
                        <h4 className="font-serif text-lg">{template.name}</h4>
                        <p className="text-xs font-mono text-muted-foreground">
                          {template.category_name && <span className="text-primary">{template.category_name} • </span>}
                          {template.content?.length || 0} {t("characters")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openConsentDialog(template)}
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteItem({ ...template, type: "consent" })}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state border py-12">
                <FileText className="empty-state-icon" />
                <p className="empty-state-title">{t("no_term_created")}</p>
              </div>
            )}
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Anamnesis Categories */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-serif text-lg">{t("anamnesis_records")}</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openCategoryDialog("anamnesis")}
                  >
                    <Plus size={14} className="mr-1" />
                    {t("new_category")}
                  </Button>
                </div>
                {anamnesisCategories.length > 0 ? (
                  <div className="space-y-2">
                    {anamnesisCategories.map((cat) => (
                      <div key={cat.id} className="border p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FolderOpen size={16} strokeWidth={1} />
                          <div>
                            <p className="font-medium">{cat.name}</p>
                            {cat.description && (
                              <p className="text-xs text-muted-foreground">{cat.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openCategoryDialog("anamnesis", cat)}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteCategory(cat)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8 border">
                    {t("no_template_categories")}
                  </p>
                )}
              </div>

              {/* Consent Categories */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-serif text-lg">{t("consent_terms")}</h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openCategoryDialog("consent")}
                  >
                    <Plus size={14} className="mr-1" />
                    {t("new_category")}
                  </Button>
                </div>
                {consentCategories.length > 0 ? (
                  <div className="space-y-2">
                    {consentCategories.map((cat) => (
                      <div key={cat.id} className="border p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FolderOpen size={16} strokeWidth={1} />
                          <div>
                            <p className="font-medium">{cat.name}</p>
                            {cat.description && (
                              <p className="text-xs text-muted-foreground">{cat.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openCategoryDialog("consent", cat)}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteCategory(cat)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8 border">
                    {t("no_template_categories")}
                  </p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Category Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingCategory ? t("edit") : t("new_template_category")}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="label-mono">{t("template_category_name")} *</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="input-paper"
                  placeholder="Ex: Procedimentos Faciais"
                />
              </div>

              <div>
                <Label className="label-mono">{t("description")}</Label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder={t("description_placeholder")}
                  rows={3}
                />
              </div>

              <Button 
                onClick={saveCategory} 
                className="btn-primary w-full"
              >
                {editingCategory ? t("save_changes") : t("create")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Category Confirmation */}
        <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl">
                {t("delete")}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("delete_template_warning")} "{deleteCategory?.name}" {t("will_be_deleted")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteCategory}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl">
                {t("delete_template")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("delete_template_warning")} "{deleteItem?.name}" {t("will_be_deleted")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminTemplates;
